import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  clearSession,
  currentSession,
  updateSessionUser,
  type SessionUser,
} from '@/api/client'
import * as authApi from '@/api/auth'
import { peutUtiliserSuperuser } from '@/navigation'
import { realtime } from '@/api/realtime'

/**
 * Source de vérité de l'identité côté interface. Le jeton, lui, reste dans
 * `api/client` : un store qui porterait aussi les jetons obligerait le client
 * HTTP à dépendre de Pinia, et donc à monter l'application entière pour tester
 * un appel réseau.
 */
export const useAuthStore = defineStore('auth', () => {
  // Hydratation depuis la session persistée : après un F5, l'utilisateur est
  // déjà connu avant tout appel réseau — pas d'écran de connexion qui clignote.
  const user = ref<SessionUser | null>(currentSession()?.user ?? null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)

  /** Autorisation grossière, alignée sur la colonne `role` en base. */
  const canWrite = computed(
    () => user.value !== null && user.value.role !== 'viewer',
  )
  const isAdmin = computed(
    () => user.value?.role === 'owner' || user.value?.role === 'admin',
  )

  /**
   * Accès au périmètre PLATEFORME (organisation de gestion).
   *
   * Miroir de `canUseSuperuser` côté serveur, exposé par le store pour que la
   * barre latérale n'ait pas à recomposer la règle. Sert à AFFICHER, jamais à
   * autoriser : chaque route du périmètre revérifie le droit.
   */
  const canSuperuser = computed(() => peutUtiliserSuperuser(user.value))

  /*
   * IL N'Y A PLUS DE `login` ICI, et c'est définitif.
   *
   * Ce store savait échanger une adresse et un mot de passe contre une
   * session. Plus aucun écran ne l'appelait depuis que les pages de connexion
   * ont été retirées — cette application n'a pas de porte, elle en reçoit un
   * laissez-passer. La garder revenait à laisser croire qu'on peut entrer
   * ici, et à maintenir un chemin qui contournerait la porte le jour où
   * quelqu'un le rebrancherait.
   *
   * L'entrée se fait par `entrerParLancement` ci-dessous : la porte
   * (`account.betool.fr`) authentifie l'identité, remet une intention à usage
   * unique, et c'est elle qu'on échange contre la session.
   */

  /**
   * Entrée par LANCEMENT — le chemin de l'écran de connexion commun (BBG-125).
   *
   * Le composant embarqué authentifie puis remet une intention à usage unique.
   * On l'échange contre la session beTool que la consommation rend depuis
   * BBG-158, et on relit `/auth/me` avec ce porteur AVANT d'enregistrer quoi
   * que ce soit : le contexte de lancement ne porte ni rôle, ni nom
   * d'organisation, et poser des valeurs d'attente le temps d'un aller-retour
   * ferait afficher une interface bâtie sur du provisoire.
   */
  async function entrerParLancement(jeton: string): Promise<boolean> {
    loading.value = true
    error.value = null
    try {
      const session = await authApi.consommerLancement(jeton)
      user.value = session.user
      realtime.connect()
      return true
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Connexion impossible.'
      return false
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    // Fermeture AVANT l'appel réseau : la déconnexion doit couper le flux même
    // si la révocation côté serveur échoue.
    realtime.disconnect()
    await authApi.logout()
    user.value = null
  }

  /**
   * Coupe la session SANS appeler le serveur. Utilisé par le gestionnaire de
   * 401 du client HTTP : le jeton est déjà refusé, un appel de plus ne ferait
   * qu'échouer.
   */
  function forgetLocally(): void {
    realtime.disconnect()
    clearSession()
    user.value = null
  }

  /**
   * Revalide la session auprès du serveur. Appelé au démarrage quand une
   * session locale existe : un jeton révoqué côté serveur (mot de passe changé
   * ailleurs, compte désactivé) doit déconnecter, pas afficher une interface
   * qui échouera à chaque action.
   */
  /**
   * Relit la session PERSISTÉE et s'y aligne.
   *
   * ─── L'INCIDENT ────────────────────────────────────────────────────────────
   *
   * `user` était lu UNE fois, à la naissance du store. Or on entre désormais par
   * un laissez-passer que l'amorçage échange en DEUX appels réseau : quand le
   * store naissait avant la fin de cet échange, il naissait sans session, et
   * plus rien ne le remplissait — `refreshUser` abandonne en silence quand il
   * n'y a pas encore de session.
   *
   * Le symptôme n'accusait pas la session : l'application s'affichait, ses
   * écrans se chargeaient (la garde de routes, elle, lit la session persistée),
   * mais la barre latérale restait VIDE — le menu se filtre sur `user.role` —
   * et l'atterrissage retombait sur l'écran de repli. Vécu en production.
   *
   * Un store dont la justesse dépend de sa DATE DE NAISSANCE est un piège :
   * celui-ci s'aligne désormais sur demande, et le résultat ne dépend plus de
   * l'ordre dans lequel les amorçages se terminent.
   */
  function hydrater(): void {
    user.value = currentSession()?.user ?? null
  }

  async function refreshUser(): Promise<void> {
    // La session a pu arriver APRÈS la naissance du store : on s'y aligne avant
    // de décider qu'il n'y a rien à rafraîchir.
    hydrater()
    if (!currentSession()) return
    try {
      const frais = await authApi.me()
      user.value = frais
      // La session persistée porte aussi l'utilisateur, et le garde du routeur
      // la lit : sans cette mise à jour, un droit tout juste accordé (ou retiré)
      // serait visible dans l'interface et ignoré par le garde.
      updateSessionUser(frais)
      // Session valide au chargement de la page : on rétablit le canal, sinon
      // un rafraîchissement d'onglet couperait le temps réel sans rien dire.
      realtime.connect()
    } catch {
      forgetLocally()
    }
  }

  return {
    entrerParLancement,
    user,
    loading,
    error,
    isAuthenticated,
    canWrite,
    isAdmin,
    canSuperuser,
    logout,
    forgetLocally,
    refreshUser,
    hydrater,
  }
})
